import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "clientSearch", "clientId", "clientList",
    "releaseSection", "releaseDropdown", "manualSongSection",
    "songName", "spotifyUrl", "releaseId", "releaseHelper",
    "genresField", "genresHelper",
    "budgetHelper",
    "releaseTypeField", "releaseTypeHelper"
  ]

  static values = {
    clients: Array
  }

  connect() {
    this.selectedClient = null
    this.boundCloseDropdown = this.closeDropdown.bind(this)
    document.addEventListener("click", this.boundCloseDropdown)

    // If editing and a client is already selected, restore state
    if (this.hasClientIdTarget && this.clientIdTarget.value) {
      const clientId = parseInt(this.clientIdTarget.value)
      this.selectedClient = this.clientsValue.find(c => c.id === clientId)
      if (this.selectedClient) {
        this.clientSearchTarget.value = `${this.selectedClient.name} ($${this.selectedClient.remaining} remaining)`
        this.populateReleases(this.selectedClient)
        this.updateBudgetHelper(this.selectedClient)
        this.updateReleaseTypeHelper(this.selectedClient)
      }
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseDropdown)
  }

  filterClients() {
    const query = this.clientSearchTarget.value.toLowerCase()
    const matches = this.clientsValue.filter(c =>
      c.name.toLowerCase().includes(query)
    )

    if (query.length === 0) {
      // Show all clients when focused with empty field
      this.renderDropdown(this.clientsValue)
    } else if (matches.length > 0) {
      this.renderDropdown(matches)
    } else {
      this.clientListTarget.innerHTML = `<div class="campaign-client-option" style="color: var(--spt-text-secondary);">No matches</div>`
      this.clientListTarget.style.display = "block"
    }
  }

  renderDropdown(clients) {
    this.clientListTarget.innerHTML = clients.map(c =>
      `<div class="campaign-client-option" data-action="click->campaign-form#selectClient" data-client-id="${c.id}">${this.esc(c.name)} <span style="color: var(--spt-text-secondary);">($${c.remaining} remaining)</span></div>`
    ).join("")
    this.clientListTarget.style.display = "block"
  }

  selectClient(event) {
    event.stopPropagation()
    const clientId = parseInt(event.currentTarget.dataset.clientId)
    this.selectedClient = this.clientsValue.find(c => c.id === clientId)
    if (!this.selectedClient) return

    this.clientSearchTarget.value = `${this.selectedClient.name} ($${this.selectedClient.remaining} remaining)`
    this.clientIdTarget.value = this.selectedClient.id
    this.clientListTarget.style.display = "none"

    this.populateReleases(this.selectedClient)
    this.autoFillGenres(this.selectedClient)
    this.autoDetectType(this.selectedClient)
    this.updateBudgetHelper(this.selectedClient)
  }

  populateReleases(client) {
    if (client.releases && client.releases.length > 0) {
      // Show release dropdown, hide manual song field
      if (this.hasReleaseSectionTarget) this.releaseSectionTarget.style.display = "block"
      if (this.hasManualSongSectionTarget) this.manualSongSectionTarget.style.display = "none"
      if (this.hasReleaseHelperTarget) this.releaseHelperTarget.style.display = "none"

      if (this.hasReleaseDropdownTarget) {
        let options = `<option value="">Select a release...</option>`
        client.releases.forEach(r => {
          const dateStr = r.release_date ? ` - ${this.esc(r.release_date)}` : ""
          options += `<option value="${r.id}" data-name="${this.esc(r.name)}" data-spotify="${this.esc(r.spotify_url || "")}">${this.esc(r.name)}${dateStr}</option>`
        })
        options += `<option value="manual">Enter song manually...</option>`
        this.releaseDropdownTarget.innerHTML = options

        // If editing and a release is already selected, restore it
        if (this.hasReleaseIdTarget && this.releaseIdTarget.value) {
          this.releaseDropdownTarget.value = this.releaseIdTarget.value
        }
      }
    } else {
      // No releases: hide dropdown, show manual field with helper message
      if (this.hasReleaseSectionTarget) this.releaseSectionTarget.style.display = "none"
      if (this.hasManualSongSectionTarget) this.manualSongSectionTarget.style.display = "block"
      if (this.hasReleaseHelperTarget) {
        this.releaseHelperTarget.textContent = "No releases found for this client. You can add one on the Releases page."
        this.releaseHelperTarget.style.display = "block"
      }
    }
  }

  releaseChanged() {
    const selected = this.releaseDropdownTarget.value

    if (selected === "manual") {
      // Switch to manual entry
      if (this.hasManualSongSectionTarget) this.manualSongSectionTarget.style.display = "block"
      if (this.hasReleaseIdTarget) this.releaseIdTarget.value = ""
      if (this.hasSongNameTarget) this.songNameTarget.value = ""
      if (this.hasSpotifyUrlTarget) this.spotifyUrlTarget.value = ""
      return
    }

    if (selected === "") {
      if (this.hasReleaseIdTarget) this.releaseIdTarget.value = ""
      if (this.hasSongNameTarget) this.songNameTarget.value = ""
      if (this.hasSpotifyUrlTarget) this.spotifyUrlTarget.value = ""
      if (this.hasManualSongSectionTarget) this.manualSongSectionTarget.style.display = "none"
      return
    }

    // Auto-fill from selected release
    const option = this.releaseDropdownTarget.selectedOptions[0]
    if (this.hasReleaseIdTarget) this.releaseIdTarget.value = selected
    if (this.hasSongNameTarget) this.songNameTarget.value = option.dataset.name || ""
    if (this.hasSpotifyUrlTarget) this.spotifyUrlTarget.value = option.dataset.spotify || ""
    if (this.hasManualSongSectionTarget) this.manualSongSectionTarget.style.display = "none"
  }

  autoFillGenres(client) {
    if (!this.hasGenresFieldTarget) return
    const genres = client.genres
    if (genres && genres.length > 0) {
      const genreText = genres.join(", ")
      this.genresFieldTarget.value = genreText
      this.genresFieldTarget.placeholder = ""
      if (this.hasGenresHelperTarget) {
        this.genresHelperTarget.textContent = "Pre-filled from client profile. Edit as needed for this release."
      }
    }
  }

  autoDetectType(client) {
    if (!this.hasReleaseTypeFieldTarget) return
    if (client.playlisting_only) {
      this.releaseTypeFieldTarget.value = "playlisting_only"
      if (this.hasReleaseTypeHelperTarget) {
        this.releaseTypeHelperTarget.textContent = "Playlisting Only: min 7 curators + weekly stacking for 4-6 weeks."
      }
    } else {
      this.releaseTypeFieldTarget.value = "pr"
      if (this.hasReleaseTypeHelperTarget) {
        this.releaseTypeHelperTarget.textContent = "PR Release: min 5 curators, one-time placement."
      }
    }
  }

  releaseTypeChanged() {
    if (!this.hasReleaseTypeHelperTarget || !this.hasReleaseTypeFieldTarget) return
    const val = this.releaseTypeFieldTarget.value
    if (val === "playlisting_only") {
      this.releaseTypeHelperTarget.textContent = "Playlisting Only: min 7 curators + weekly stacking for 4-6 weeks."
    } else {
      this.releaseTypeHelperTarget.textContent = "PR Release: min 5 curators, one-time placement."
    }
  }

  // The read-only monthly line under the client picker: the client's
  // envelope, not an input. Budgets are monthly and per client; they live
  // on the client page (money-clarity committee v2).
  updateBudgetHelper(client) {
    if (!this.hasBudgetHelperTarget) return
    const resetDay = client.budget_reset_day || 1
    const suffix = this.ordinalSuffix(resetDay)
    this.budgetHelperTarget.textContent = `${client.name} has $${client.remaining} of $${client.monthly} monthly left, resets on the ${resetDay}${suffix} (${client.days_until_reset} days).`
    this.budgetHelperTarget.className = "spt-helper"
    this.budgetHelperTarget.style.display = "block"
  }

  updateReleaseTypeHelper(client) {
    if (!this.hasReleaseTypeHelperTarget || !client) return
    if (client.playlisting_only) {
      this.releaseTypeHelperTarget.textContent = "Playlisting Only: min 7 curators + weekly stacking for 4-6 weeks."
    } else {
      this.releaseTypeHelperTarget.textContent = "PR Release: min 5 curators, one-time placement."
    }
  }

  closeDropdown(event) {
    if (this.hasClientListTarget && !this.element.contains(event.target)) {
      this.clientListTarget.style.display = "none"
    }
  }

  clearSearch() {
    // When user focuses and input has a selected client, clear for new search
    if (this.selectedClient && this.clientSearchTarget.value.includes("remaining)")) {
      this.clientSearchTarget.value = ""
      this.filterClients()
    }
  }

  ordinalSuffix(n) {
    const s = ["th", "st", "nd", "rd"]
    const v = n % 100
    return s[(v - 20) % 10] || s[v] || s[0]
  }

  esc(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
